/**
 * Core Sudoku Game Types
 *
 * All TypeScript types and interfaces for the Sudoku puzzle game.
 * Board: 9×9 grid divided into nine 3×3 boxes.
 * Single-player puzzle: fill empty cells with digits 1-9.
 */

// ============================================================================
// Board Configuration
// ============================================================================

/** A single cell on the Sudoku board */
export interface SudokuCell {
  /** Current value (1-9) or null if empty */
  value: number | null;
  /** Whether this cell was part of the original puzzle (non-editable) */
  isGiven: boolean;
  /** Pencil marks / candidate notes */
  notes: number[];
  /** Whether this cell has a conflict */
  isError: boolean;
}

/** 9×9 Sudoku board */
export type SudokuBoard = SudokuCell[][];

/** Difficulty levels */
export type SudokuDifficulty = 'easy' | 'medium' | 'hard';

/** Board position */
export interface SudokuPosition {
  row: number;
  col: number;
}

// ============================================================================
// Move History
// ============================================================================

export interface SudokuMove {
  position: SudokuPosition;
  previousValue: number | null;
  newValue: number | null;
  previousNotes: number[];
  newNotes: number[];
  type: 'place' | 'remove' | 'note';
  timestamp: number;
}

// ============================================================================
// Game State
// ============================================================================

export interface SudokuGameState {
  /** Current board state */
  board: SudokuBoard;
  /** The complete solution for validation */
  solution: number[][];
  /** Difficulty of the current puzzle */
  difficulty: SudokuDifficulty;
  /** Currently selected cell position, or null */
  selectedCell: SudokuPosition | null;
  /** Elapsed time in seconds */
  timer: number;
  /** History of moves for undo */
  moveHistory: SudokuMove[];
  /** Number of hints used */
  hintsUsed: number;
  /** Current game status */
  gameStatus: 'idle' | 'playing' | 'paused' | 'completed';
  /** Whether notes mode is active */
  notesMode: boolean;
  /** Unique game identifier */
  gameId: string;
  /** Timestamp when the game started */
  startedAt: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface SudokuGameSettings {
  difficulty: SudokuDifficulty;
}

// ============================================================================
// Statistics
// ============================================================================

export interface SudokuDifficultyStats {
  gamesPlayed: number;
  gamesWon: number;
  bestTime: number | null;
  averageTime: number;
  totalHintsUsed: number;
  winPercentage: number;
}

export interface SudokuStatistics {
  userId?: string;
  easy: SudokuDifficultyStats;
  medium: SudokuDifficultyStats;
  hard: SudokuDifficultyStats;
  totalGames: number;
  totalPlayTime: number;
  currentStreak: number;
  bestStreak: number;
}

export interface SudokuGameResult {
  gameId: string;
  difficulty: SudokuDifficulty;
  completed: boolean;
  time: number;
  hintsUsed: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface SudokuGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface SudokuGameModeSelectorProps {
  onStartGame: (settings: SudokuGameSettings) => void;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

export interface SudokuBoardProps {
  board: SudokuBoard;
  selectedCell: SudokuPosition | null;
  onCellClick: (position: SudokuPosition) => void;
  disabled?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

export const BOARD_SIZE = 9;
export const BOX_SIZE = 3;

/** Number of given clues per difficulty */
export const DIFFICULTY_CLUES: Record<SudokuDifficulty, { min: number; max: number }> = {
  easy: { min: 35, max: 40 },
  medium: { min: 28, max: 34 },
  hard: { min: 22, max: 27 },
};
